import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { moveArrayItemToNewIndex } from '../../../lib/js/Array';
import {
  focusBlockHeadlineByIndex,
  indexIn,
  isLayoutGroup,
  layoutGroupChanged,
  notInOther,
} from './ContentWrapper.utils';
import { focusElementWithId } from '../../../lib/DomUtils/utils';
import usePrevious from '../../../hooks/usePrevious';
import { GlobalDocumentIds } from '../../../types/GlobalDocumentIds';
import { sendLiveRegionMessage } from '../../atoms/LiveRegionGlobal/LiveRegionGlobal';
import { Namespace } from '../../../translation/namespace';
import { isNotNullOrUndefined } from '../../../lib/typeGuards';
import { ContentWrapperAction, ContentWrapperBlocks, ContentWrapperError } from './ContentWrapper.types';
import { idCreator } from '../../organisms/BlockInteractions';

/** ErrorState variable in ContentWrapper
 *
 * holds a type and a uuid which the corresponding block
 * can match on its own uuid.
 */
export type ErrorState = {
  /** uuid of the block being moved, deleted,... Is undefined when created */
  uuid?: string;
  type?: keyof typeof ContentWrapperError;
};

/** ActionState variable in ContentWrapper
 *
 * holds a type (`ContentWrapperAction`) and a uuid
 * which the corresponding block can match on its own uuid.
 */
export type ActionState = {
  /** uuid of the block being moved, deleted,... Is undefined when created */
  uuid?: string;
  type?: keyof typeof ContentWrapperAction;
};

/** Responsible for moving blocks
 *
 * Responsible for
 * - adding blocks (and focus)
 * - moving blocks (and focus)
 *   - injecting actions into the blocks
 * - removing blocks
 *   - set focus on previous block
 * - connecting blocks
 */
export function useContentWrapper(initialBlocks: ContentWrapperBlocks[], fetchUrl: string) {
  const { t } = useTranslation(Namespace.ContentWrapper);
  const [blocks, setBlocks] = useState<ContentWrapperBlocks[]>(() => initialBlocks);
  const [action, setAction] = useState<ActionState | null>(null);
  const [error, setError] = useState<ErrorState | null>(null);
  const [visualNumber, setVisualNumber] = useState<number>();

  // snapshot of block order before user started moving them via drag an drop
  // -> our way of dnd is by changing the state on every change, not by defining different drop regions.
  // -> this is why we need one snapshot to reset to when something goes wrong.
  const blockSnapShot = useRef<ContentWrapperBlocks[]>(null);

  const createSnapshotOfBlockOrder = () => {
    blockSnapShot.current = blocks;
  };

  const moveBlockToBlock = (sourceId: string, targetId: string) => {
    const sourceIdIndex = blocks.findIndex(b => b.uuid === sourceId);
    const targetIdIndex = blocks.findIndex(b => b.uuid === targetId);
    const newBlocks = moveArrayItemToNewIndex(blocks, sourceIdIndex, targetIdIndex).filter(isNotNullOrUndefined);
    setBlocks(newBlocks);
  };

  const submitNewBlockToBlockMove = (blockUuid: string) => {
    const movedBlock = blocks.find(block => block.uuid === blockUuid);
    // @ts-expect-error move block url is here !
    const moveBlockUrl = movedBlock?.actions?.moveBlockDown?.url || movedBlock.actions?.moveBlockUp?.url;
    const oldPosition = blockSnapShot.current?.findIndex(b => b.uuid === blockUuid);
    const newPosition = blocks.findIndex(b => b.uuid === blockUuid);

    if (moveBlockUrl && oldPosition !== undefined && oldPosition !== newPosition) {
      const formdata = new FormData();
      formdata.set('step', `${newPosition - oldPosition}`);
      setError(null);
      axios
        .post(moveBlockUrl, formdata)
        .then(() => {
          // focus block headline on drop
          setVisualNumber(newPosition + 1);
        })
        .catch(() => {
          setError({ uuid: blockUuid, type: ContentWrapperError.moveFail });
          if (blockSnapShot.current) {
            setBlocks(blockSnapShot.current);
          }
        });
    }
  };

  /**
   * Because of batching feature of React 18 we focusing block should be part of the state.
   * */
  useEffect(() => {
    if (visualNumber) {
      focusBlockHeadlineByIndex(visualNumber);
    }
  }, [visualNumber]);

  const prevBlocks = usePrevious<ContentWrapperBlocks[]>(blocks);
  const addBlock = useCallback((newBlock: ContentWrapperBlocks, position: number) => {
    const getNewBlocks = (blks: ContentWrapperBlocks[], index: number) => [
      ...blks.slice(0, index),
      newBlock,
      ...blks.slice(index),
    ];
    setBlocks(blks => getNewBlocks(blks, position));
  }, []);

  /**
   * refetches the ContentWrappers.
   * Takes an uuid so that the corresponding block can display the Error
   */
  const refetch = async (uuid: string) => {
    const res = await axios.get(fetchUrl);
    if (res.status >= 200 && res.status < 300 && res.data?.views?.default?.embedded?.content) {
      setBlocks(res.data.views.default.embedded.content);
    } else {
      setError({ uuid, type: ContentWrapperError.fetchError });
    }
  };

  const onMovingFailure = (uuid: string) => {
    setAction(null);
    setError({ uuid, type: ContentWrapperError.moveFail });
  };

  const onMovingSuccess = (actionKey: string, uuid: string, elementType: string) => {
    const blockIndex = blocks.findIndex(b => b.uuid === uuid);
    const newBlockIndex = actionKey === ContentWrapperAction.moveBlockUp ? blockIndex - 1 : blockIndex + 1;

    const visualIndex = newBlockIndex + 1;
    sendLiveRegionMessage(t(`moveSuccess.${elementType}`, { position: visualIndex }));

    const newBlocks = moveArrayItemToNewIndex(blocks, blockIndex, newBlockIndex).filter(isNotNullOrUndefined);
    setBlocks(newBlocks);
    setAction(null);
    setError(null);
    setVisualNumber(visualIndex);
  };

  /**
   * When a block is deleted successfully in the backend the following happens:
   * - the block is deleted in the frontend
   * - the previous block will be focused
   * - if there is no previous block, the addContentBlock will be focused
   */
  const onDeleteSuccess = (uuid: string) => {
    const deletedBlockIndex = blocks.findIndex(block => block.uuid === uuid);

    const blockToBeFocussed = deletedBlockIndex === 0 ? blocks[deletedBlockIndex] : blocks[deletedBlockIndex - 1];

    const allBlocks = [...blocks];
    allBlocks.splice(deletedBlockIndex, 1);

    setBlocks(allBlocks);

    if (allBlocks.length !== 0) {
      const id = idCreator(blockToBeFocussed.elementType, blockToBeFocussed.uuid);
      focusElementWithId(id);
    } else {
      focusElementWithId(GlobalDocumentIds.addContentBlock);
    }
  };

  useEffect(() => {
    setBlocks(initialBlocks);
  }, [initialBlocks]);

  /**
   * Handle focus when
   * - block was added -> focus added block
   * - block is removed -> focus prev block
   * - layoutgroup is created -> focus on layoutgroup
   * - layoutgroup got disassembled -> focus first child block
   */
  useEffect(() => {
    if (prevBlocks) {
      const oldGroups = prevBlocks.filter(isLayoutGroup);
      const newGroups = blocks.filter(isLayoutGroup);

      // we have created a new LayoutGroup
      if (newGroups.length > oldGroups.length) {
        const added = oldGroups.length === 0 ? newGroups[0] : notInOther(newGroups, oldGroups);
        const index = indexIn(blocks, added);
        focusBlockHeadlineByIndex(index + 1);
        return;
      }

      // we have removed a LayoutGroup
      if (newGroups.length < oldGroups.length) {
        const removed = notInOther(oldGroups, newGroups);
        const index = indexIn(prevBlocks, removed);
        focusBlockHeadlineByIndex(index + 1);
        return;
      }

      // we have added a block
      const addedBlock = notInOther(blocks, prevBlocks);
      if (addedBlock) {
        const index = indexIn(blocks, addedBlock);
        focusBlockHeadlineByIndex(index + 1);
        return;
      }

      // we have removed a block
      const removedBlock = notInOther(prevBlocks, blocks);
      if (removedBlock) {
        const index = indexIn(prevBlocks, removedBlock);
        if (blocks.length === 0) {
          // if we do not have any blocks left, focus <AddContentBlock />
          focusElementWithId(GlobalDocumentIds.addContentBlock);
        } else {
          focusBlockHeadlineByIndex(index);
        }
        return;
      }

      // a LayoutGroup changed
      if (newGroups.length === oldGroups.length) {
        const layoutgroup = layoutGroupChanged(newGroups, oldGroups);
        if (layoutgroup) {
          const index = indexIn(blocks, layoutgroup);
          focusBlockHeadlineByIndex(index + 1);
        }
      }
    }
  }, [blocks, prevBlocks]);

  return {
    blocks,
    addBlock,
    action,
    setAction,
    error,
    setError,
    refetch,
    onMovingSuccess,
    onMovingFailure,
    onDeleteSuccess,
    submitNewBlockToBlockMove,
    moveBlockToBlock,
    createSnapshotOfBlockOrder,
  };
}
